import { useCallback, useEffect } from 'react';
import { isApolloError } from '@apollo/client';

import { useDeletedUserLoginPopup } from '@ocx-app/components/modals';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { authService } from '@ocx-app/modules/auth/instances/authService';

import { AuthMethod, SignupWithTokenMutation, useSignupWithTokenMutation } from '@ocx/graphql';
import { getErrorWithExtensionInfo } from '@ocx/apollo-client';
import { useConfiguration } from '@ocx/data-configuration';

import { useSignupEvents } from '../../use-signup-events.hook';
import { OC_MEMBERSHIP_DELETED_ERROR } from '../../signup.constants';

export type LoginByTokenControllerParams = {
  onCompleted(): void;
  onError(error: Error): void;
};

export const useSignupWithLinkController = (
  sessionId: string,
  { onCompleted, onError }: LoginByTokenControllerParams,
) => {
  const { triggerSignupSessionCreated, triggerMagicLinkInvalid } = useSignupEvents();
  const { open: openDeletedUserLoginPopup } = useDeletedUserLoginPopup();
  const { pushRequestLink } = useGetNavigationActions();

  const handleCompleted = useCallback(
    ({
      signupWithToken: {
        tokens: { accessToken, refreshToken },
      },
    }: SignupWithTokenMutation) => {
      authService.setTokens(accessToken, refreshToken);
      triggerSignupSessionCreated({ method: AuthMethod.MagicLink });
      onCompleted();
    },
    [onCompleted, triggerSignupSessionCreated],
  );

  const handleError = useCallback(
    (error: Error) => {
      if (
        isApolloError(error) &&
        getErrorWithExtensionInfo(error.graphQLErrors)?.extensionCode === OC_MEMBERSHIP_DELETED_ERROR
      ) {
        openDeletedUserLoginPopup({ onClosed: pushRequestLink });
        return;
      }
      triggerMagicLinkInvalid();
      onError(error);
    },
    [onError, triggerMagicLinkInvalid, openDeletedUserLoginPopup, pushRequestLink],
  );

  const [signupWithToken] = useSignupWithTokenMutation({
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const chainId = useConfiguration('chainId');
  const brandUuid = useConfiguration('brandUuid');
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    signupWithToken({
      variables: {
        input: {
          sessionId,
          brandUuid,
          chainId,
        },
      },
    });
  }, [brandUuid, chainId, sessionId, signupWithToken]);
};
